import * as R from 'ramda';
import { Task } from '../types';
import { BlockType } from './block-type.constants';
import { ExecutionStatus } from './execution-status.constants';
import { ExecutionType } from './execution-type.constants';

export class VisualisationExecutionTypeWrapper {
    static readonly DRY = new VisualisationExecutionTypeWrapper(
        ExecutionType.Dry,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? ``
                : `Structure of sample data up to the ${outputBlock.displayName} output block has been evaluated`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? ``
                : `Structure of sample data up to the ${outputBlock.displayName} output block is currently under evalutation`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? ``
                : `Structure of sample data up to the ${outputBlock.displayName} output block is in queue to be evaluated`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? ``
                : `Evaluation of structure for sample data up to the ${outputBlock.displayName} output block has been cancelled`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? ``
                : `Evaluation of structure for sample data up to the ${outputBlock.displayName} output block has failed`,
    );

    static readonly TEST = new VisualisationExecutionTypeWrapper(
        ExecutionType.Test,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? `Training (fitting) of ${task.displayName} is completed`
                : `Sample data from executing analytics pipeline up to the ${outputBlock.displayName} output block has been calculated`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? `Training (fitting) of ${task.displayName} is in progress`
                : `Sample data from executing analytics pipeline up to the ${outputBlock.displayName} output block is currently being calculated`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? `Training (fitting) of ${task.displayName} is in queue to be performed`
                : `Sample data from executing analytics pipeline up to the ${outputBlock.displayName} output block is in queue to be calculated`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? `Training (fitting) of ${task.displayName} has been cancelled`
                : `Evaluation of sample data from executing analytics pipeline up to the ${outputBlock.displayName} output block has been cancelled`,
        (task: Task, outputBlock: Task) =>
            task.block.type === BlockType.Train
                ? `Training (fitting) of ${task.displayName} has failed`
                : `Evaluation of sample data from executing analytics pipeline up to the ${outputBlock.displayName} output block has failed`,
    );

    static readonly NORMAL = new VisualisationExecutionTypeWrapper(
        ExecutionType.Normal,
        () => `Run of analytics pipeline has completed`,
        () => `Run of analytics pipeline is currently in progress`,
        () => `Run of analytics pipeline is queued for execution`,
        () => `Run of analytics pipeline has been cancelled`,
        () => `Run of analytics pipeline has failed`,
    );

    private constructor(
        public readonly type: ExecutionType,
        private readonly completedFunc: any,
        private readonly runningFunc: any,
        private readonly queuedFunc: any,
        private readonly cancelledFunc: any,
        private readonly failedFunc: any,
    ) {
        this.type = type;
        this.completedFunc = completedFunc;
        this.runningFunc = runningFunc;
        this.queuedFunc = queuedFunc;
        this.cancelledFunc = cancelledFunc;
        this.failedFunc = failedFunc;
    }

    static all(): VisualisationExecutionTypeWrapper[] {
        return [this.DRY, this.TEST, this.NORMAL];
    }

    static find(type: ExecutionType | string): VisualisationExecutionTypeWrapper | null {
        for (let t = 0; t < VisualisationExecutionTypeWrapper.all().length; t++) {
            const typeWrapper = VisualisationExecutionTypeWrapper.all()[t];
            if (typeWrapper.type === type) {
                return typeWrapper;
            }
        }
        return null;
    }

    completed(task?: Task | null, outputBlock?: Task | null): string {
        return this.completedFunc(task, outputBlock);
    }

    running(task?: Task | null, outputBlock?: Task | null): string {
        return this.runningFunc(task, outputBlock);
    }

    queued(task?: Task | null, outputBlock?: Task | null): string {
        return this.queuedFunc(task, outputBlock);
    }

    cancelled(task?: Task | null, outputBlock?: Task | null): string {
        return this.cancelledFunc(task, outputBlock);
    }

    failed(task?: Task | null, outputBlock?: Task | null): string {
        return this.failedFunc(task, outputBlock);
    }

    message(status: ExecutionStatus, task?: Task | null, message?: string | null, outputBlock?: Task | null) {
        if (status === ExecutionStatus.Completed) {
            return { message: this.completed(task, outputBlock), category: 'Success' };
        }
        if (status === ExecutionStatus.Running) {
            return { message: this.running(task, outputBlock), category: 'Running' };
        }
        if (status === ExecutionStatus.Queued) {
            return { message: this.queued(task, outputBlock), category: 'Queued' };
        }
        if (status === ExecutionStatus.Cancelled) {
            return { message: this.cancelled(task, outputBlock), category: 'Cancelled' };
        }
        if (status === ExecutionStatus.Failed) {
            return {
                message: !R.isNil(message)
                    ? `${this.failed(task, outputBlock)}: ${message}`
                    : this.failed(task, outputBlock),
                category: 'Error',
            };
        }
        return { message: null, category: null };
    }
}
